import { useMemo, useState, useEffect } from 'react';

import { ButtonColors } from 'types';
import { Icon } from 'components/icon';
import { ButtonWithRef } from 'components/button';

import * as Styles from './styles';
import { MenuItem, MenuProps } from './types';

// The top bar and a gap under it.
const TOP_BAR_ROOM = 96;

const commonProps = {
  weight: 400,
  padding: '8px',
  fullWidth: true,
  colorType: ButtonColors.BlackWhite,
};

const Menu = ({
  data,
  buttonProps,
  buttonAlignStart,
  keepTooltipInside,
  belowTopBar,
  ...rest
}: MenuProps) => {
  const [optionData, setOptionData] = useState<MenuItem[]>();

  const renderData = useMemo(() => optionData ?? data, [data, optionData]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [optionData]);

  const maxHeight = useMemo(() => {
    // Owner 24 Oct (rule 1): the POS menu opens under the top bar; half the
    // screen cut its last item on an iPad in landscape.
    let height: number = belowTopBar
      ? window.innerHeight - TOP_BAR_ROOM
      : window.innerHeight / 2;
    if (typeof keepTooltipInside === 'string') {
      const selector = document.querySelector(keepTooltipInside);
      if (selector) {
        height = selector.getBoundingClientRect().height / 2;
      }
    }
    return height;
  }, [keepTooltipInside, belowTopBar, window.innerHeight]);

  return (
    <Styles.Popup
      width="178px"
      repositionOnResize
      keepTooltipInside={keepTooltipInside}
      trigger={<ButtonWithRef {...buttonProps} />}
      {...rest}
    >
      {(close: () => void) => (
        <Styles.Container
          maxHeight={maxHeight}
          onClick={(e) => e.stopPropagation()}
        >
          {optionData && (
            <Styles.StyledButton
              justifyContent="flex-start"
              icon={<Icon.ChevronLeft />}
              onClick={() => setOptionData(undefined)}
              {...commonProps}
            />
          )}
          <Styles.List maxHeight={maxHeight}>
            {renderData.map((value, idx) => {
              const { onClick, data: itemData, ...props } = value;

              return (
                <Styles.StyledButton
                  key={`option_${idx}`}
                  rightIcon={itemData ? <Icon.ChevronRight /> : undefined}
                  justifyContent={
                    buttonAlignStart ? 'flex-start' : 'space-between'
                  }
                  onClick={() => {
                    if (itemData) {
                      setOptionData(itemData);
                    } else {
                      onClick?.();
                      setOptionData(undefined);
                      close();
                    }
                  }}
                  {...commonProps}
                  {...props}
                />
              );
            })}
          </Styles.List>
        </Styles.Container>
      )}
    </Styles.Popup>
  );
};

export { Menu };
export type { MenuItem, MenuProps };
